import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  Paper,
  Checkbox,
  CircularProgress,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import dayjs from 'dayjs';
import axios from 'axios';
import DatePicker from 'react-multi-date-picker';
import { Hotel } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  API_ALLOCATE_ROOMS,
  API_GET_AVAILABLE_ROOMS_LIST,
} from '../../../Util/URLConstants';
import { align_center, align_left, VM_Colors } from '../../../Styles/VMStyles';
import MUIAlertDialog from '../../../Components/MUIAlertDialog';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PROFILE, SESSION_USER_COUNTRY_DATA_KEY, SESSION_USER_ID_KEY } from '../../../Util/VMConstants';
import StorageFactory from '../../../Util/StorageFactory';
import { formatAmount } from '../../../Util/UtilHelper';

const AllocateRooms = ({
  customer,
  handleBookRoom,
  hideDateSearch = false,
  externalTabs = null,
  onAllocationSuccess,
  allocateButtonLabel = 'Allocate Rooms',
  showBottomAllocateButton = false,
  showTopContinueButton = false,
  onContinue,
}) => {
  const [selectedDates, setSelectedDates] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [rooms, setRooms] = useState([]);
  const [selectedRoomsByDate, setSelectedRoomsByDate] = useState({}); // per-date selection
  const [loading, setLoading] = useState(false);
  const [allowedDates, setAllowedDates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState("");
  const navigate = useNavigate();

  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const currencySymbol = sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || '₹';


  useEffect(() => {
    if (customer?.appointment_date?.length) {
      const deduplicated = Array.from(new Set(customer.appointment_date));
      const formatted = deduplicated.map((d) => dayjs(d).format('DD-MM-YYYY'));
      setAllowedDates(formatted);
    } else {
      setAllowedDates([]);
    }
  }, [customer]);

  // helper to extract readable error text from API response
  const getErrorText = (respData) => {
    if (!respData) return 'Something went wrong';
    if (typeof respData === 'string') return respData;
    if (typeof respData.message === 'string') return respData.message;
    if (respData.message && typeof respData.message.message === 'string') return respData.message.message;
    try { return JSON.stringify(respData.message || respData); } catch (e) { return 'Something went wrong'; }
  };

  // count selected slots (for button label)
  const countSelectedSlots = () => {
    return Object.values(selectedRoomsByDate).reduce((sum, roomsObj) => {
      return (
        sum +
        Object.values(roomsObj).reduce((roomSum, slotsObj) => roomSum + Object.keys(slotsObj).length, 0)
      );
    }, 0);
  };

  // immutable update for selecting/unchecking rooms — normalize roomId to string
  const toggleRoomSelection = (date, roomId, slotKey, check_in, check_out, is_next_day) => {
    const roomKey = String(roomId);
    setSelectedRoomsByDate((prev) => {
      const updated = { ...prev };

      if (!updated[date]) updated[date] = {};
      else updated[date] = { ...updated[date] };

      if (!updated[date][roomKey]) updated[date][roomKey] = {};
      else updated[date][roomKey] = { ...updated[date][roomKey] };

      if (updated[date][roomKey][slotKey]) {
        // Uncheck → remove slot
        const { [slotKey]: _, ...restSlots } = updated[date][roomKey];
        updated[date][roomKey] = restSlots;

        if (Object.keys(updated[date][roomKey]).length === 0) {
          delete updated[date][roomKey];
        }
        if (Object.keys(updated[date]).length === 0) {
          delete updated[date];
        }
      } else {
        // Check → add slot (default guestCount 0)
        updated[date][roomKey][slotKey] = {
          check_in,
          check_out,
          slot_name: slotKey,
          is_next_day,
          guestCount: 0,
        };
      }

      return updated;
    });
  };

  // immutable guest count update — normalize roomId to string
  const updateGuestCount = (date, roomId, slotKey, count) => {
    const roomKey = String(roomId);
    setSelectedRoomsByDate((prev) => {
      const updated = { ...prev };

      if (!updated[date]) return prev;
      updated[date] = { ...updated[date] };
      if (!updated[date][roomKey]) return prev;
      updated[date][roomKey] = { ...updated[date][roomKey] };
      if (!updated[date][roomKey][slotKey]) return prev;

      updated[date][roomKey][slotKey] = {
        ...updated[date][roomKey][slotKey],
        guestCount: count,
      };

      return updated;
    });
  };

  // fetch rooms — convert date to API-expected format (YYYY-MM-DD) and show dialog for invalid time slots
  const handleFetchRooms = async (tabIndex) => {
    const selectedDate = tabs[tabIndex];
    if (!selectedDate) return;

    setLoading(true);
    setRooms([]);
    try {
      // convert from DD-MM-YYYY to YYYY-MM-DD (adjust per backend requirements)
      const apiDate = dayjs(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD');

      const response = await axios.get(
        `${API_GET_AVAILABLE_ROOMS_LIST}?user_id=${userID}&date=${apiDate}`
      );
      const respData = response.data;

      if (respData.status_code === 200) {
        setRooms(respData.grouped_rooms || []);
      } else {
        const errText = getErrorText(respData);
        console.error('Rooms API returned error:', respData);
        if (errText.toLowerCase().includes('invalid time slots') || errText.toLowerCase().includes('invalid timeslots')) {
          setDialogContent("⚠️ The selected date has invalid time slots. Please add the time slots.");
          setDialogOpen(true);
        } else {
          toast.error(errText);
        }
        setRooms([]);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error?.response?.data || error);
      // if server returned structured error in error.response.data
      const serverErr = error?.response?.data;
      if (serverErr) {
        const errText = getErrorText(serverErr);
        if (errText.toLowerCase().includes('invalid time slots') || errText.toLowerCase().includes('invalid timeslots')) {
          setDialogContent("⚠️ The selected date has invalid time slots. Please add the time slots.");
          setDialogOpen(true);
        } else {
          toast.error(errText);
        }
      } else {
        toast.error('Failed to load room list');
      }
      setRooms([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAllocate = () => {
    const allocations = [];

    Object.entries(selectedRoomsByDate).forEach(([date, roomsForDate]) => {
      Object.entries(roomsForDate).forEach(([roomId, slots]) => {
        const room = rooms.find((r) => String(r.room_id) === String(roomId));
        if (!room) return;

        const baseCost = parseInt(room.room_cost, 10) || 0;
        const guestCostPerPerson = parseInt(room.guest_cost, 10) || 0;

        Object.values(slots).forEach(({ check_in, check_out, is_next_day, slot_name, guestCount }) => {
          const totalGuestCost = guestCount * guestCostPerPerson;
          const bookedRoomCost = baseCost + totalGuestCost;

          allocations.push({
            room_id: roomId,
            room_name: room.room_name,
            enquiry_id: customer.appointment_id,
            user_id: userID,
            slot_name,
            event_check_in: check_in,
            event_check_out: check_out,
            guest_count: guestCount,
            guest_cost: guestCostPerPerson,
            booked_room_cost: bookedRoomCost,
            date
          });
        });
      });
    });

    if (allocations.length === 0) {
      toast.error('Please select at least one room slot');
      return;
    }

    setLoading(true);
    axios.post(API_ALLOCATE_ROOMS, { allocations }, {
      headers: { 'Content-Type': 'application/json' },
    }).then((response) => {
      const respData = response.data;
      setLoading(false);
      if (respData.status_code === 200) {
        const successText = typeof respData.message === 'string' ? respData.message : getErrorText(respData);
        toast.success(successText);
        setSelectedRoomsByDate({});
        if (onAllocationSuccess) {
          onAllocationSuccess();
        } else if (handleBookRoom) {
          handleBookRoom();
        }
      } else {
        const errText = getErrorText(respData);
        if (errText.toLowerCase().includes('invalid time slots') || errText.toLowerCase().includes('invalid timeslots')) {
          setDialogContent("⚠️ Allocation failed due to invalid time slots. Please try again with valid slots or contact support.");
          setDialogOpen(true);
        } else {
          toast.error(errText);
        }
      }
    }).catch((error) => {
      setLoading(false);
      console.error('Allocation error:', error?.response?.data || error);
      const serverErr = error?.response?.data;
      if (serverErr) {
        const errText = getErrorText(serverErr);
        if (errText.toLowerCase().includes('invalid time slots') || errText.toLowerCase().includes('invalid timeslots')) {
          setDialogContent("⚠️ Allocation failed due to invalid time slots. Please try again with valid slots or contact support.");
          setDialogOpen(true);
        } else {
          toast.error(errText);
        }
      } else {
        toast.error('Something went wrong while allocating rooms');
      }
    });
  };

  const handleSearch = () => {
    if (!selectedDates.length) {
      toast.error('Please select at least one date');
      return;
    }

    const formattedDates = selectedDates.map((d) => dayjs(d).format('DD-MM-YYYY'));
    setTabs(formattedDates);
    setActiveTab(0);
  };

  const handleTabChange = (_, newIndex) => {
    setActiveTab(newIndex);
    handleFetchRooms(newIndex);
  };

  useEffect(() => {
    if (externalTabs != null && Array.isArray(externalTabs)) {
      setTabs(externalTabs);
      setActiveTab(0);
    }
  }, [externalTabs]);

  useEffect(() => {
    if (tabs.length > 0) {
      handleFetchRooms(0);
    } else {
      setRooms([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs]);

  const groupedRooms = rooms.reduce((acc, room) => {
    const type = room.room_type || "Other";
    if (!acc[type]) acc[type] = [];
    acc[type].push(room);
    return acc;
  }, {});

  const handleOkClick = () => {
    setDialogOpen(false);
    navigate("/" + ROUTE_PROFILE);
  };

  const currentDate = tabs[activeTab];
  const selectedCount = countSelectedSlots();

  const allocateButtonSx = {
    backgroundColor: VM_Colors.primary,
    color: VM_Colors.white,
    '&:hover': { backgroundColor: '#162e66', color: VM_Colors.white },
  };

  const renderAllocateButton = () =>
    selectedCount > 0 ? (
      <Button
        variant="contained"
        onClick={handleAllocate}
        disabled={loading}
        sx={allocateButtonSx}
      >
        {allocateButtonLabel} ({selectedCount})
      </Button>
    ) : null;

  return (
    <Box p={2} sx={{ minHeight: 100 }}>
      {!hideDateSearch && (
        <Box p={2} component={Paper} elevation={1}>
          <Grid container spacing={3} alignItems="center" justifyContent="space-between" direction={isSmallScreen ? 'column' : 'row'}>
            <Grid item xs={12} sm={4}>
              <Box>
                <DatePicker
                  multiple
                  label="Check-In & Check-Out Dates"
                  value={selectedDates}
                  onChange={setSelectedDates}
                  format="DD-MM-YYYY"
                  placeholder="DD-MM-YYYY"
                  minDate={new Date()}
                  style={{
                    width: '100%',
                    minWidth: 250,
                    height: 40,
                    backgroundColor: '#f5f5f5',
                    fontSize: '16px',
                    padding: '4px 12px',
                    borderRadius: 6,
                  }}
                />
                {allowedDates.length > 0 && (
                  <Box mt={1} display="flex" alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                      ℹ️ Booked dates : <b>{allowedDates.join(', ')}</b>
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box sx={align_left} display="flex" justifyContent={isSmallScreen ? 'flex-start' : 'flex-end'} mt={isSmallScreen ? 2 : 0}>
                <Button variant="contained" color="primary" onClick={handleSearch} sx={{ minWidth: 150, height: 40, fontWeight: 600 }}>
                  Search
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {tabs.length > 0 && (
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            TabIndicatorProps={{ style: { height: 3 } }}
            sx={{
              marginTop: 1,
              minHeight: 'auto',
              flex: 1,
              minWidth: 0,
              '& .MuiTabs-flexContainer': { flexWrap: 'nowrap' },
            }}
          >
            {tabs.map((date, idx) => (
              <Tab
                key={idx}
                label={date}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 1,
                  width: 100,
                  minWidth: 'auto',
                  padding: '6px 12px',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease-in-out',
                  color: activeTab === idx ? VM_Colors.white : VM_Colors.black,
                  backgroundColor: activeTab === idx ? VM_Colors.primary : VM_Colors.white1,
                  '&:hover': {
                    backgroundColor: activeTab === idx ? VM_Colors.primary : VM_Colors.white2,
                    color: activeTab === idx ? VM_Colors.white : VM_Colors.black,
                  },
                  '&.Mui-selected': {
                    color: VM_Colors.white,
                  },
                }}
              />
            ))}
          </Tabs>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {showTopContinueButton && onContinue && (
              <Button variant="outlined" onClick={onContinue}>
                Continue
              </Button>
            )}
            {renderAllocateButton()}
          </Box>
        </Box>
      )}

      {/* Room cards */}
      <Box sx={{ minHeight: 300 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
        ) : rooms.length === 0 ? (
          <Typography sx={{ mt: 5, ...align_center }}>
            {/* No Rooms Found. Please select dates and click Search. */}
          </Typography>
        ) : (
          Object.entries(groupedRooms).map(([roomType, roomList], index) => (
            <Box key={index} sx={{ mt: 3 }}>
              <Typography variant="h4" sx={{ mb: 2 }}><b>{roomType}</b></Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  overflowX: 'auto',
                  gap: 2,
                  pb: 1,
                  '&::-webkit-scrollbar': { height: 8 },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: 4 },
                }}
              >
                {roomList.map((room) => {
                  const selectedRoomSlots = selectedRoomsByDate[currentDate]?.[String(room.room_id)] || {};
                  const slots = [
                    { key: 'slot0', label: 'Slot 1', check_in: room.check_in, check_out: room.check_out, is_next_day: room.is_next_day, isBooked: room.isAlreadyBooked },
                    { key: 'slot1', label: 'Slot 2', check_in: room.check_in1, check_out: room.check_out1, is_next_day: room.is_next_day1, isBooked: room.isAlreadyBooked1 }
                  ].filter(s => s.check_in && s.check_out);

                  return (
                    <Paper key={room.room_id} sx={{ p: 2, minWidth: 240, border: '2px dashed grey', backgroundColor: 'white', flexShrink: 0, userSelect: 'none', '&:hover': { boxShadow: 4 } }}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Hotel color="primary" />
                        <Box ml={1}><Typography variant="subtitle1">{room.room_name}</Typography></Box>
                      </Box>
                      <Typography variant="body2">Type: {room.room_type}</Typography>
                      <Typography variant="body2">Base Cost: {formatAmount( room.room_cost,currencySymbol)}</Typography>
                      <Typography variant="body2">Guest Cost per Person: {formatAmount(room.guest_cost,currencySymbol)}</Typography>

                      {slots.map((slot) => {
                        const isSelected = !!selectedRoomSlots[slot.key];
                        const guestCount = selectedRoomSlots[slot.key]?.guestCount || 0;
                        const baseCost = parseInt(room.room_cost, 10) || 0;
                        const guestCost = parseInt(room.guest_cost, 10) || 0;
                        const totalCost = baseCost + guestCount * guestCost;

                        return (
                          <Box
                            key={slot.key}
                            mt={1}
                            px={1}
                            py={1}
                            border="2px solid"
                            borderColor={slot.isBooked ? VM_Colors.error : isSelected ? VM_Colors.success : 'grey.300'}
                            borderRadius={2}
                            bgcolor={slot.isBooked ? VM_Colors.error_transparent : isSelected ? 'rgba(0,128,0,0.05)' : 'white'}
                            display="flex"
                            flexDirection="column"
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Box>
                                <Typography variant="body2" fontWeight={600}>{slot.label}</Typography>
                                <Typography variant="caption">
                                  {dayjs(slot.check_in).format('YYYY-MM-DD hh:mm A')} - {dayjs(slot.check_out).format('YYYY-MM-DD hh:mm A')}
                                </Typography>
                                {slot.isBooked && (
                                  <Typography variant="caption" color="error" fontWeight={600}>
                                    &nbsp;Already Booked
                                  </Typography>
                                )}
                              </Box>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                onChange={() => {
                                  if (!slot.isBooked) {
                                    toggleRoomSelection(currentDate, room.room_id, slot.key, slot.check_in, slot.check_out, slot.is_next_day);
                                  }
                                }}
                                disabled={slot.isBooked}
                              />
                            </Box>

                            {isSelected && !slot.isBooked && (
                              <>
                                <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                                  <Typography variant="body2">Extra Guests:</Typography>
                                  <Box display="flex" alignItems="center">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => updateGuestCount(currentDate, room.room_id, slot.key, Math.max(0, guestCount - 1))}
                                      disabled={guestCount <= 0}
                                    >
                                      -
                                    </Button>
                                    <Typography mx={1}>{guestCount}</Typography>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => updateGuestCount(currentDate, room.room_id, slot.key, Math.min(9, guestCount + 1))}
                                      disabled={guestCount >= 9}
                                    >
                                      +
                                    </Button>
                                  </Box>
                                </Box>

                                <Box mt={1}>
                                  <Typography variant="body2">Total Cost: {formatAmount( totalCost,currencySymbol)}</Typography>
                                </Box>
                              </>
                            )}
                          </Box>
                        );
                      })}
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          ))
        )}
      </Box>

      {showBottomAllocateButton && selectedCount > 0 && (
        <Box display="flex" justifyContent="center" gap={2} mt={3} flexWrap="wrap">
          {onContinue && (
            <Button variant="outlined" onClick={onContinue}>
              Continue
            </Button>
          )}
          {renderAllocateButton()}
        </Box>
      )}

      {/* Dialog */}
      <MUIAlertDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        message={dialogContent}
        okClickHandler={handleOkClick}
      />
    </Box>
  );
};

export default AllocateRooms;
